import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Mail, Phone, Clock, Send } from 'lucide-react';
import { PageHero, Reveal, SectionTitle } from '../components/common';
import { garden, faqs, img } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: 'Message sent!', description: `Thank you, ${form.name}. We'll be in touch soon.` });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <PageHero image={img.gardenPath} kicker="Contact" title="We'd love to hear from you" sub="Questions, partnership ideas, or ready to get involved? Reach out and connect with the garden." />

      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          {/* Form */}
          <Reveal>
            <SectionTitle kicker="Send a Message" title="Get in touch" />
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-600 text-forest mb-2">Name</label>
                  <input required value={form.name} onChange={upd('name')} className="w-full px-4 py-3 rounded-lg border border-sand bg-white focus:border-gold focus:outline-none text-sm" placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-sm font-600 text-forest mb-2">Email</label>
                  <input required type="email" value={form.email} onChange={upd('email')} className="w-full px-4 py-3 rounded-lg border border-sand bg-white focus:border-gold focus:outline-none text-sm" placeholder="you@email.com" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-600 text-forest mb-2">Subject</label>
                <input required value={form.subject} onChange={upd('subject')} className="w-full px-4 py-3 rounded-lg border border-sand bg-white focus:border-gold focus:outline-none text-sm" placeholder="How can we help?" />
              </div>
              <div>
                <label className="block text-sm font-600 text-forest mb-2">Message</label>
                <textarea required rows={5} value={form.message} onChange={upd('message')} className="w-full px-4 py-3 rounded-lg border border-sand bg-white focus:border-gold focus:outline-none text-sm resize-none" placeholder="Tell us more..." />
              </div>
              <button type="submit" disabled={sending} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-white font-600 text-sm hover:bg-gold-light transition-colors disabled:opacity-60">{sending ? 'Sending...' : 'Send Message'} <Send className="w-4 h-4" /></button>
            </form>
          </Reveal>

          {/* Info */}
          <Reveal delay={150}>
            <div className="bg-forest text-cream rounded-2xl p-8 lg:p-10">
              <h3 className="font-display text-2xl text-white">Visit & connect</h3>
              <div className="mt-8 space-y-6">
                {[
                  { icon: MapPin, label: 'Location', value: garden.address },
                  { icon: Phone, label: 'Phone', value: garden.phone },
                  { icon: Mail, label: 'Email', value: garden.email },
                  { icon: Clock, label: 'Opening', value: `${garden.opening} · previews for members` },
                ].map((r) => (
                  <div key={r.label} className="flex gap-4">
                    <div className="w-11 h-11 rounded-xl bg-forest-light/40 flex items-center justify-center shrink-0">
                      <r.icon className="w-5 h-5 text-gold-light" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gold-light font-600">{r.label}</div>
                      <div className="text-cream/85 mt-1">{r.value}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 rounded-xl overflow-hidden h-52">
                <iframe title="map" className="w-full h-full border-0" loading="lazy" src="https://www.google.com/maps?q=College+Park+Georgia&output=embed" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-sand">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="FAQ" title="Questions, answered" /></Reveal>
          <Reveal delay={100}>
            <Accordion type="single" collapsible className="mt-10 space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border border-sand px-5">
                  <AccordionTrigger className="text-left font-display text-lg text-forest hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-ink/65 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
